import {
  index,
  integer,
  sqliteTable,
  text,
  uniqueIndex,
} from "drizzle-orm/sqlite-core";
import { z } from "zod";
import { createdAt, id, updatedAt } from "../lib/types";
import { cuid, dateTime, string } from "../validator/common";
import { sharedContentTopics } from "./content";

export enum TagType {
  GENERAL = 0,
  POWERLIFTING = 1,
  BODYBUILDING = 2,
  NUTRITION = 3,
  RECOVERY = 4,
  MENTAL = 5,
  EQUIPMENT = 6,
  TECHNIQUE = 7,
  COMMUNITY = 8,
  EVENTS = 9,
  OTHER = 10,
}

export const tagTypeNamesList = [
  "General",
  "Powerlifting",
  "Bodybuilding",
  "Nutrition",
  "Recovery",
  "Mental",
  "Equipment",
  "Technique",
  "Community",
  "Events",
  "Other",
] as const;

export function getTagTypeName(type: TagType): string {
  return tagTypeNamesList[type];
}

export type TagTypeName = (typeof tagTypeNamesList)[number];

export const forumModerationStatuses = [
  "pending_review",
  "published",
  "archived",
  "merged",
] as const;

export type ForumModerationStatus = (typeof forumModerationStatuses)[number];

export const tags = sqliteTable(
  "tags",
  {
    tagId: id,
    createdId: text("created_by").notNull(),
    tagTitle: text("tag_name").notNull(),
    slug: text("slug"),
    category: text("category")
      .default("General")
      .notNull()
      .$type<TagTypeName>(),
    tagContent: text("tag_content").notNull(),
    topics: text("topics", { mode: "json" })
      .$type<string[]>()
      .default([])
      .notNull(),
    ctaHref: text("cta_href"),
    ctaLabel: text("cta_label"),
    moderationStatus: text("moderation_status", {
      enum: forumModerationStatuses,
    })
      .default("pending_review")
      .notNull(),
    acceptedMessageId: text("accepted_message_id"),
    approvedAt: integer("approved_at", { mode: "timestamp" }),
    approvedBy: text("approved_by"),
    mergedIntoId: text("merged_into_id"),
    lastActivityAt: integer("last_activity_at", { mode: "timestamp" }),
    viewCount: integer("view_count").default(0).notNull(),
    updatedAt,
    createdAt,
  },
  (t) => [
    uniqueIndex("unique_conversation_id").on(t.tagId),
    uniqueIndex("unique_forum_question_slug").on(t.slug),
    index("idx_forum_questions_status_activity").on(
      t.moderationStatus,
      t.lastActivityAt,
    ),
  ],
);

export const selectTagschema = z.object({
  tagId: cuid,
  createdId: cuid,
  tagTitle: string,
  slug: z.string().nullable().optional(),
  category: z.enum([
    "General",
    "Powerlifting",
    "Bodybuilding",
    "Nutrition",
    "Recovery",
    "Mental",
    "Equipment",
    "Technique",
    "Community",
    "Events",
    "Other",
  ]),
  tagContent: string,
  topics: z.array(z.enum(sharedContentTopics)).default([]),
  ctaHref: z.string().nullable().optional(),
  ctaLabel: z.string().max(80).nullable().optional(),
  moderationStatus: z.enum(forumModerationStatuses),
  acceptedMessageId: cuid.nullable().optional(),
  approvedAt: dateTime.nullable().optional(),
  approvedBy: cuid.nullable().optional(),
  mergedIntoId: cuid.nullable().optional(),
  lastActivityAt: dateTime.nullable().optional(),
  viewCount: z.number().int().nonnegative().default(0),
  updatedAt: dateTime,
  createdAt: dateTime,
});

export const insertTagschema = z.object({
  tagTitle: string.min(3, "Tag title must be at least 3 characters long"),
  tagContent: string.min(10, "Tag content must be at least 10 characters long"),
  category: z.enum([
    "General",
    "Powerlifting",
    "Bodybuilding",
    "Nutrition",
    "Recovery",
    "Mental",
    "Equipment",
    "Technique",
    "Community",
    "Events",
    "Other",
  ]),
  topics: z.array(z.enum(sharedContentTopics)).default([]).optional(),
});

export const patchTagschema = z.object({
  tagId: cuid,
  tagTitle: string.min(3, "Tag title must be at least 3 characters long"),
  tagContent: string.min(10, "Tag content must be at least 10 characters long"),
  category: z.enum([
    "General",
    "Powerlifting",
    "Bodybuilding",
    "Nutrition",
    "Recovery",
    "Mental",
    "Equipment",
    "Technique",
    "Community",
    "Events",
    "Other",
  ]),
  topics: z.array(z.enum(sharedContentTopics)).optional(),
  ctaHref: z
    .string()
    .regex(/^\/(?!\/)/, "CTA yalnız site içi bir adres olabilir")
    .nullable()
    .optional(),
  ctaLabel: z.string().trim().max(80).nullable().optional(),
});

export const moderateForumQuestionSchema = z.object({
  tagId: cuid,
  status: z.enum(forumModerationStatuses),
  mergedIntoId: cuid.nullable().optional(),
});
